/**
 * Composable for image gallery navigation and slideshow functionality
 */

import { ref, computed, watch, onUnmounted, type Ref, type ComputedRef } from 'vue'
import type { ImageAssetListItem } from '../../types/asset'

export interface UseImageNavigationOptions {
  items: Ref<ImageAssetListItem[]>
  initialIndex?: number
  loop?: boolean
  shuffle?: boolean
  slideshowDuration?: number // seconds
  onIndexChange?: (index: number) => void
  onSlideshowToggle?: (active: boolean) => void
  onSlideshowPause?: (paused: boolean) => void
}

export interface UseImageNavigationReturn {
  // State
  currentIndex: Ref<number>
  currentItem: ComputedRef<ImageAssetListItem | undefined>
  totalItems: ComputedRef<number>

  // Navigation computed
  canGoNext: ComputedRef<boolean>
  canGoPrev: ComputedRef<boolean>
  isFirstItem: ComputedRef<boolean>
  isLastItem: ComputedRef<boolean>

  // Navigation methods
  goToIndex: (index: number) => void
  next: () => void
  prev: () => void
  first: () => void
  last: () => void

  // Slideshow state
  slideshowActive: Ref<boolean>
  slideshowPaused: Ref<boolean>

  // Slideshow methods
  startSlideshow: () => void
  stopSlideshow: () => void
  toggleSlideshow: () => void
  pauseSlideshow: () => void
  resumeSlideshow: () => void
}

/**
 * Composable for managing image navigation and slideshow
 */
export function useImageNavigation(options: UseImageNavigationOptions): UseImageNavigationReturn {
  const {
    items,
    initialIndex = 0,
    loop = true,
    shuffle = false,
    slideshowDuration = 5,
    onIndexChange,
    onSlideshowToggle,
    onSlideshowPause,
  } = options

  // Navigation state
  const currentIndex = ref(initialIndex)

  // Slideshow state
  const slideshowActive = ref(false)
  const slideshowPaused = ref(false)
  let slideshowTimer: ReturnType<typeof setTimeout> | null = null

  // Shuffle state
  const shuffleOrder = ref<number[]>([])
  let shuffleIndex = 0

  // Computed
  const totalItems = computed(() => items.value.length)

  const currentItem = computed(() => items.value[currentIndex.value])

  const canGoNext = computed(() => {
    if (totalItems.value === 0) return false
    return loop || currentIndex.value < totalItems.value - 1
  })

  const canGoPrev = computed(() => {
    if (totalItems.value === 0) return false
    return loop || currentIndex.value > 0
  })

  const isFirstItem = computed(() => currentIndex.value === 0)
  const isLastItem = computed(() => currentIndex.value === totalItems.value - 1)

  /**
   * Generates a shuffled order of indices
   */
  function generateShuffleOrder(): void {
    const indices = Array.from({ length: totalItems.value }, (_, i) => i)
    // Fisher-Yates shuffle
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[indices[i], indices[j]] = [indices[j], indices[i]]
    }
    shuffleOrder.value = indices
    shuffleIndex = 0
  }

  /**
   * Navigates to a specific index
   */
  function goToIndex(index: number): void {
    if (totalItems.value === 0) return

    let validIndex = index

    if (loop) {
      // Wrap around
      if (index < 0) {
        validIndex = totalItems.value - 1
      } else if (index >= totalItems.value) {
        validIndex = 0
      }
    } else {
      // Clamp to bounds
      validIndex = Math.max(0, Math.min(index, totalItems.value - 1))
    }

    if (validIndex !== currentIndex.value) {
      currentIndex.value = validIndex
      onIndexChange?.(validIndex)
    }
  }

  /**
   * Navigates to the next image
   */
  function next(): void {
    if (!canGoNext.value) return

    if (shuffle && slideshowActive.value) {
      // Use shuffle order during slideshow
      shuffleIndex = (shuffleIndex + 1) % shuffleOrder.value.length
      goToIndex(shuffleOrder.value[shuffleIndex])
    } else {
      goToIndex(currentIndex.value + 1)
    }
  }

  /**
   * Navigates to the previous image
   */
  function prev(): void {
    if (!canGoPrev.value) return

    if (shuffle && slideshowActive.value) {
      // Use shuffle order during slideshow
      shuffleIndex = (shuffleIndex - 1 + shuffleOrder.value.length) % shuffleOrder.value.length
      goToIndex(shuffleOrder.value[shuffleIndex])
    } else {
      goToIndex(currentIndex.value - 1)
    }
  }

  /**
   * Navigates to the first image
   */
  function first(): void {
    goToIndex(0)
  }

  /**
   * Navigates to the last image
   */
  function last(): void {
    goToIndex(totalItems.value - 1)
  }

  // Slideshow methods
  function clearSlideshowTimer(): void {
    if (slideshowTimer) {
      clearTimeout(slideshowTimer)
      slideshowTimer = null
    }
  }

  function scheduleSlideshowNext(): void {
    clearSlideshowTimer()
    if (slideshowActive.value && !slideshowPaused.value) {
      slideshowTimer = setTimeout(() => {
        next()
        scheduleSlideshowNext()
      }, slideshowDuration * 1000)
    }
  }

  function startSlideshow(): void {
    if (totalItems.value <= 1) return
    if (slideshowActive.value) return

    if (shuffle) {
      generateShuffleOrder()
      // Find current index in shuffle order
      shuffleIndex = shuffleOrder.value.indexOf(currentIndex.value)
      if (shuffleIndex === -1) shuffleIndex = 0
    }

    slideshowActive.value = true
    slideshowPaused.value = false
    onSlideshowToggle?.(true)
    scheduleSlideshowNext()
  }

  function stopSlideshow(): void {
    if (!slideshowActive.value) return

    clearSlideshowTimer()
    slideshowActive.value = false
    slideshowPaused.value = false
    onSlideshowToggle?.(false)
  }

  function toggleSlideshow(): void {
    if (slideshowActive.value) {
      stopSlideshow()
    } else {
      startSlideshow()
    }
  }

  function pauseSlideshow(): void {
    if (!slideshowActive.value || slideshowPaused.value) return

    clearSlideshowTimer()
    slideshowPaused.value = true
    onSlideshowPause?.(true)
  }

  function resumeSlideshow(): void {
    if (!slideshowActive.value || !slideshowPaused.value) return

    slideshowPaused.value = false
    onSlideshowPause?.(false)
    scheduleSlideshowNext()
  }

  // Watch for items changes
  watch(
    () => items.value.length,
    (newLength) => {
      // Reset index if out of bounds
      if (currentIndex.value >= newLength) {
        currentIndex.value = Math.max(0, newLength - 1)
      }
      // Regenerate shuffle order if shuffling
      if (shuffle && slideshowActive.value) {
        generateShuffleOrder()
      }
    },
  )

  // Cleanup on unmount
  onUnmounted(() => {
    clearSlideshowTimer()
  })

  return {
    // State
    currentIndex,
    currentItem,
    totalItems,

    // Navigation computed
    canGoNext,
    canGoPrev,
    isFirstItem,
    isLastItem,

    // Navigation methods
    goToIndex,
    next,
    prev,
    first,
    last,

    // Slideshow state
    slideshowActive,
    slideshowPaused,

    // Slideshow methods
    startSlideshow,
    stopSlideshow,
    toggleSlideshow,
    pauseSlideshow,
    resumeSlideshow,
  }
}
